import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {ResourceType} from 'common/report/constants';
import {BookmarkDateRange, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Segmentation} from 'common/types/reports/query';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {ComparisonDateRangeBookmarkFormat} from 'common/query-builder/query-block/time-comparison-menu/types';
import {CohortFilterTree} from 'common/query-builder/query-block/filter-menu/types';
import {FiltersOperator} from 'common/query-builder/query-entry-section/types';
import {Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {
  ChartType,
  RetentionAlignmentType,
  RetentionType,
  RetentionUnboundedModeType,
  SortConfig,
} from 'common/types/reports/bookmark';
import {TableCell as DataTableCell, TableRow} from 'common/components/data-table/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';

export const NON_INTERVALIZED_VALUE = `non_intervalized`;

// We could have used `null` to represented non-intervalized rather than a special `non_intervalized` value. I've
// opted for a special value as I feel it's less prone to bugs where the date_unit is missing by mistake (and not
// being able to distinguish that from non-intervalized)
export type BirthIntervalUnit = TimeUnit | typeof NON_INTERVALIZED_VALUE;

export type RetentionChartType =
  // deprecated chart type for retention curve
  | ChartType.RetentionLine
  // AKA retention curve
  | ChartType.RetentionTable
  | ChartType.RetentionTrend
  | ChartType.RetentionTrendMetric;

export enum RetentionApiChartType {
  Line = `line`,
  Table = `table`,
  RetentionTrend = `retention-trend`,
  RetentionTrendMetric = `retention-trend-metric`,
}

/**
 * Relative date range.
 *
 * This was the persisted bookmark format from 2014-10-23 to 2019-05-28.
 *
 * @example {to: 0, from: -14} // last 2 weeks
 */
export type RelativeRollingDateRange = {
  to: number;
  from: number;
};

export type RetentionBookmarkFilterByEvent = {
  operator: string;
  children: Array<
    | {
        [ResourceType.Event]: Segmentation;
      }
    | {
        [ResourceType.User]: Segmentation;
      }
  >;
};

export interface RetentionBookmarkParams {
  /**
   * Date range for the query.
   *
   * Bookmarks saved after 2019-05-28 have BookmarkDateRange
   * Bookmarks saved after 2014-10-23 have RelativeRollingDateRange
   * Bookmarks saved before do not have a date_range.
   */
  date_range?: BookmarkDateRange | RelativeRollingDateRange;
  date_unit: BirthIntervalUnit;
  // Not used by Frequency report, which uses date_unit for figuring out both birth interval unit and retention unit
  retention_unit?: TimeUnit;
  /**
   * Event for birth retention.
   *
   * Some actual null, some string 'null', some real strings.
   * */
  born_event?: null | `null` | string;
  /**
   * Main query event.
   *
   * Sometimes set to empty string between 2012-01-17 and 2015-01-10.
   * Sometimes set to null between 2014-11-25 and 2016-02-18.
   * Rarely set to 'null' between 2014 - 2015 (6 times).
   */
  event: string | '' | null;

  // it looks like in the past only a single selection was allowed.
  // post QB, this is a full cohort tree
  filter_by_cohort?: CohortFilterTree | EmptyObj;
  /** new format for breakdowns */
  group_by?: Segment[];
  /**
   * deprecated way of storing breakdowns as arb selector string
   *
   * @example `properties["$browser"]`
   */
  on?: string;

  filters?: Segfilter[] | Segmentation[];
  filters_op?: FiltersOperator;
  born_filters?: Segfilter[] | Segmentation[];
  born_filters_op?: FiltersOperator;
  bucket_sizes?: number[];

  /** deprecated way of storing cohorts for either filter or breakdown */
  cohorts?: Array<{id: number; name: string}>;
  include_negated_cohorts?: boolean;
  segmentation_event?: string;

  /**
   * which datagroup is active.
   */
  data_group_id?:
    | `` // none selected
    | `0` // none selected (deprecated way)
    | string // stringified number
    | null; // deprecated

  retention_type: RetentionType;
  chart_type?: RetentionApiChartType;
  cumulative?: boolean;
  unbounded_retention_mode?: RetentionUnboundedModeType;
  percentage_view?: boolean;
  selected_segments?: string[];
  expanded_segments?: string[];
  sort_column?: string;
  sort_desc?: boolean;
  sorting?: SortConfig;
  retention_bucket?: number;
  dashboard?: DashboardOrigin;
  limit?: number;

  time_comparison?: ComparisonDateRangeBookmarkFormat;

  lift_comparison?: LiftComparison;

  /** A link back to the report. For dashboard cards. */
  url_hash?: string;

  filter_by_event?: EmptyObj | RetentionBookmarkFilterByEvent;

  retention_alignment_type?: RetentionAlignmentType;
}

/*
 * It doesn't make sense to infer things like dropoff rate, etc. with Lift data (since the value is always a decimal).
 * This type helps us carry the right info around without the confusion of all of those derived fields
 */
export interface LiftCell {
  complete_date: string | false;
  // TODO: Currently entered_value is the segment name if it's a segmented query and the date if it's not.
  // We should split it into separate fields for segment and date so it's easier for clients to use the info.
  entered_value: string;
  incomplete: boolean;
  value: number;
  /** This would be lift value of the Retention Rate*/
  percent: number;
  formatted_percent: string;
  /** Date of birth interval */
  date?: Date;
}

/*
 * Because FormattedRow is bound to RawCell, need another row type for Lift
 */
export interface LiftRow {
  date?: Date;
  /** raw_head, but truncated to fit in a cell*/
  display_head: string;
  /** stringified head */
  raw_head: string;
  /** identifier for the row: Date if unsegmented, segment string if segmented */
  head: Date | string;
  /** actual retention data */
  data: LiftCell[];
  groupedRows?: LiftRow[];
}

// TODO: See if we can delete the formatted_ fields
export interface RawCell {
  absolute_drop_off_value: number;
  formatted_absolute_drop_off_value: string;
  formatted_value: string;
  cohort_size: number;
  complete_date: string | false;
  // TODO: Currently entered_value is the segment name if it's a segmented query and the date if it's not.
  // We should split it into separate fields for segment and date so it's easier for clients to use the info.
  entered_value: string;
  incomplete: boolean;
  percent: number;
  formatted_percent_drop_off_value: string;
  percent_drop_off_value: number;
  formatted_percent: string;
  selectable: boolean;
  value: number;
  /** Date of birth interval */
  date?: Date;
}
export interface TableCell extends RawCell {
  backgroundColor?: string;
  textColor?: string;
  clickable?: boolean;
  tooltip?: string;
}

export type CellType = TableCell | RawCell;

/**
 * TableRow for the old retention-table
 */

export interface FormattedRow<T extends CellType = RawCell> {
  date?: Date;
  /** number of users who entered this bucket */
  cohort_size: number;
  /** cohort_size, but commaized and ready for display */
  display_cohort_size: string;
  /** raw_head, but truncated to fit in a cell*/
  display_head: string;
  /** stringified head */
  raw_head: string;
  /** identifier for the row: Date if unsegmented, segment string if segmented */
  head: Date | string;
  /** color for the row */
  selection_color: string;
  /** actual retention data */
  data: T[];
  /** rows which are grouped to this one. In actuality are just the date rows */
  groupedRows?: Array<FormattedRow<T>>;
  /** A matching show clause index of a row */
  matching_show_clause_index?: number;
}

export type RetentionDataTableCell = DataTableCell & TableCell;

export interface RetentionDataTableRow extends TableRow<RetentionDataTableCell> {
  /** number of users who entered this bucket */
  cohortSize: number;
  /** cohortSize, but commaized and ready for display */
  displayCohortSize: string;
  /** rawHead, but truncated to fit in a cell*/
  displayHead: string;
  /** stringified head */
  rawHead: string;
  /** birth interval for this row */
  date: Date;
  /** identifier for the row: Date if unsegmented, segment string if segmented */
  head: Date | string;
  /** if this row is in a parent's "groupedRows" */
  isGroupedRow: boolean;
  /** matching show clause index of a row */
  matchingShowClauseIndex: number;
}

export function isRelativeRollingDateRange(dateRange: any): dateRange is RelativeRollingDateRange {
  if (!dateRange) {
    return false;
  }
  return !(`type` in dateRange) && (`to` in dateRange || `from` in dateRange);
}

export function isRetentionTrendBasedChart(chartType: ChartType) {
  return chartType === ChartType.RetentionTrend || chartType === ChartType.RetentionTrendMetric;
}

export function isRetentionCurveBasedChart(chartType: ChartType) {
  return chartType === ChartType.RetentionLine || chartType === ChartType.RetentionTable;
}

export interface MetricKeyInfo {
  isHidden: boolean;
  showClauseIndex: number;
}
